// ABOUTME: Hold, the second state of 4A: a veil over the scene with the new Make It Right task, where it lands in Responsibilities, and one pill.
// ABOUTME: It owns its controls (the pill and nothing else), takes focus on its heading when it appears, and covers the dimmed plate's scene.

import { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  CROSSING_BUTTON_FILL, CROSSING_GOLD_INK, CROSSING_HEADLINE_INK, CROSSING_HOLD_VEIL,
} from '../../../../theme/crossingTokens';
import type { CrossingWithProgress } from '../../../../types/api';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import type { RunnerApi } from '../../runner/screenContract';
import { RUNNER_PLATE_CLEARANCE_PX } from '../../runner/plateClearance';
import MakeItRightTaskTile from './MakeItRightTaskTile';
import ResponsibilitiesCard from './ResponsibilitiesCard';
import { ADDS_TO, HOLD_EYEBROW, HOLD_HEADLINE, HOLD_SUB } from './makeItRightCopy';

const HOLD_Z = 4; // covers the cloud stack (1) and sits under the plate (10)

interface MakeItRightHoldProps {
  crossing: CrossingWithProgress;
  runner: RunnerApi;
  confirmActionId: string;
}

export default function MakeItRightHold({ crossing, runner, confirmActionId }: MakeItRightHoldProps) {
  const reducedMotion = useCrossingReducedMotion();
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Focus holds after the press that brought Hold up, after a reload, and after Back from 4B.
  useEffect(() => { headingRef.current?.focus(); }, []);

  const hold = crossing.makeItRightHold;
  // The pill's sentence is the definition's label for the action, so it is read, not retyped.
  const label = crossing.progress.actions.find(action => action.id === confirmActionId)?.label ?? '';

  return (
    <Box
      data-testid="make-it-right-hold"
      sx={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: `-${RUNNER_PLATE_CLEARANCE_PX}px`, zIndex: HOLD_Z,
        overflowY: 'auto', backgroundColor: CROSSING_HOLD_VEIL, boxSizing: 'border-box',
        p: 3, pb: `calc(${RUNNER_PLATE_CLEARANCE_PX}px + max(24px, var(--plate-overhang, 0px)))`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center',
        animation: reducedMotion ? 'none' : 'makeItRightHoldFadeIn 0.5s ease-out',
        '@keyframes makeItRightHoldFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}
    >
      <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '2px', color: CROSSING_GOLD_INK }}>{HOLD_EYEBROW}</Typography>
      <Typography
        ref={headingRef}
        component="h2"
        data-overlay-heading
        tabIndex={-1}
        sx={{ fontFamily: 'Newsreader, serif', fontSize: '22px', fontWeight: 400, lineHeight: 1.4, color: CROSSING_HEADLINE_INK, maxWidth: 560, outline: 'none' }}
      >
        {HOLD_HEADLINE}
      </Typography>
      <Typography sx={{ fontSize: '15px', lineHeight: 1.5, color: CROSSING_HEADLINE_INK, maxWidth: 560 }}>{HOLD_SUB}</Typography>

      {hold && (
        <Box sx={{ width: '100%', maxWidth: 820, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MakeItRightTaskTile list={hold.tile} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: 'center', justifyContent: 'center', gap: 0.5, color: CROSSING_GOLD_INK }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '2px' }}>{ADDS_TO}</Typography>
            <Typography aria-hidden sx={{ fontSize: '20px', lineHeight: 1, transform: { xs: 'rotate(90deg)', sm: 'none' } }}>→</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ResponsibilitiesCard
              newList={{ id: hold.tile.id, title: hold.tile.title, completedItems: hold.tile.completedItems, totalItems: hold.tile.totalItems }}
              otherLists={hold.otherLists}
            />
          </Box>
        </Box>
      )}

      <Button
        variant="contained"
        data-action-id={confirmActionId}
        aria-disabled={runner.saving || undefined}
        onClick={() => { if (!runner.saving) runner.run(confirmActionId); }}
        sx={{
          width: '100%', maxWidth: 560, mt: 1, borderRadius: '999px', px: 3, py: 1.25, textTransform: 'none', lineHeight: 1.35,
          backgroundColor: CROSSING_BUTTON_FILL,
          '&[aria-disabled="true"]': { opacity: 0.55, cursor: 'default' },
        }}
      >
        {label}
      </Button>
    </Box>
  );
}
